import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Download, FileText, MessageCircle, Mail, TrendingDown, Plus, Check, Utensils, Trash2, Home } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import Nav from "@/components/site/Nav";
import { Input } from "@/components/ui/input";
import { FoodSearchPanel } from "@/components/site/FoodSearchPanel";
import { API_BASE } from "@/lib/api";
import { PHASES, TRACKERS } from "@/data/diet";

const phaseIdx = (phaseId) => PHASES.findIndex((p) => p.name.toLowerCase().replace(/ /g, "_") === phaseId);

function BudgetBar({ label, value, target, unit }) {
  const pct = Math.min(100, Math.round((value / target) * 100));
  const over = value > target;
  return (
    <div data-testid={`budget-${label.toLowerCase()}`}>
      <div className="flex justify-between text-xs mb-1">
        <span className="font-semibold text-[#2D372B]">{label}</span>
        <span className={over ? "text-[#A05252] font-semibold" : "text-[#5C665A]"}>
          {value} / {target} {unit} {over ? "· over" : `· ${pct}%`}
        </span>
      </div>
      <div className="h-2.5 rounded-full bg-[#E5E2D9] overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${over ? "bg-[#A05252]" : "bg-[#89937C]"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function TrackerSection() {
  const [date, setDate] = useState(new Date().toLocaleDateString("en-CA"));
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);

  const load = () =>
    axios.get(`${API_BASE}/diet/client/tracker?date=${date}`, { withCredentials: true })
      .then((r) => setData(r.data)).catch(() => setData({ items: [], totals: null }));

  useEffect(() => { load(); }, [date]); // eslint-disable-line
  useEffect(() => {
    axios.get(`${API_BASE}/diet/client/tracker/recent`, { withCredentials: true })
      .then((r) => setRecent(r.data.recent)).catch(() => {});
  }, []);

  const search = async (q) => {
    const r = await axios.get(`${API_BASE}/diet/foods/search?q=${encodeURIComponent(q)}`, { withCredentials: true });
    return r.data.results;
  };

  const add = async (item) => {
    try {
      await axios.post(`${API_BASE}/diet/client/tracker`, { ...item, date }, { withCredentials: true });
      toast.success(`${item.name} (${item.serving}) logged`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not log food"); }
  };

  const remove = async (id) => {
    try { await axios.delete(`${API_BASE}/diet/client/tracker/${id}`, { withCredentials: true }); load(); }
    catch { toast.error("Could not remove"); }
  };

  return (
    <div className="mt-8 bg-white border border-[#E5E2D9] rounded-3xl p-7" data-testid="client-food-tracker">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-serif-display text-2xl flex items-center gap-2"><Utensils size={18} className="text-[#89937C]" /> Food Search & Daily Tracker</h2>
        <input type="date" data-testid="client-tracker-date" value={date} onChange={(e) => setDate(e.target.value)}
          className="bg-[#F7F5F0] border border-[#E5E2D9] rounded-xl h-10 px-3 text-sm" />
      </div>
      <p className="text-xs text-[#5C665A] mt-1 mb-4">Search any food (powered by FatSecret), set your exact portion in grams and log it — your coach sees the same tracker.</p>

      {data?.targets?.kcal && data.totals && (
        <div className="mb-4 space-y-3 rounded-2xl bg-[#F7F5F0] p-4" data-testid="budget-bars">
          <BudgetBar label="Calories" value={data.totals.calories} target={data.targets.kcal} unit="kcal" />
          {data.targets.protein ? <BudgetBar label="Protein" value={data.totals.protein} target={data.targets.protein} unit="g" /> : null}
        </div>
      )}

      {recent.length > 0 && (
        <div className="mb-4" data-testid="recent-foods">
          <div className="text-[10px] font-bold tracking-[0.15em] text-[#C57B57] uppercase mb-1.5">Quick re-log</div>
          <div className="flex flex-wrap gap-1.5">
            {recent.map((r, i) => (
              <button key={i} data-testid={`recent-food-${i}`} onClick={() => add(r)}
                className="rounded-full border border-[#E5E2D9] bg-white px-3 py-1.5 text-[11px] text-[#5C665A] hover:border-[#C57B57] hover:text-[#2D372B] transition-colors">
                + {r.name}{r.serving ? ` (${r.serving})` : ""} · {r.calories} kcal
              </button>
            ))}
          </div>
        </div>
      )}

      <FoodSearchPanel search={search} onAdd={add} placeholder="Search a food you ate — e.g. dal, apple, grilled chicken…" />

      <div className="mt-5 pt-4 border-t border-[#F0EEE6]">
        {!data ? <Loader2 size={16} className="animate-spin text-[#89937C]" /> : data.items.length === 0 ? (
          <p className="text-sm text-[#9AA294]" data-testid="client-tracker-empty">Nothing tracked for this day yet.</p>
        ) : (
          <>
            <ul className="space-y-2" data-testid="client-tracker-items">
              {data.items.map((i) => (
                <li key={i.id} className="flex items-center justify-between gap-3 border border-[#F0EEE6] rounded-xl px-4 py-2.5 text-sm">
                  <span className="min-w-0 truncate">{i.name}{i.serving ? <span className="text-[#9AA294]"> · {i.serving}</span> : ""}</span>
                  <span className="shrink-0 flex items-center gap-3">
                    <span className="text-xs text-[#5C665A]">{i.calories} kcal · P {i.protein}g · C {i.carbs}g · F {i.fat}g</span>
                    <button data-testid={`client-tracker-del-${i.id}`} onClick={() => remove(i.id)} className="text-[#A05252] hover:opacity-70" aria-label="Remove"><Trash2 size={13} /></button>
                  </span>
                </li>
              ))}
            </ul>
            {data.totals && (
              <div className="mt-3 rounded-xl bg-[#2D372B] text-[#F7F5F0] px-4 py-3 text-sm font-medium" data-testid="client-tracker-totals">
                Day total: <span className="text-[#C57B57] font-bold">{data.totals.calories} kcal</span> · Protein {data.totals.protein}g · Carbs {data.totals.carbs}g · Fat {data.totals.fat}g
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function DietClient() {
  const [state, setState] = useState({ loading: true, client: null, error: null });
  const [p, setP] = useState({ weight_kg: "", waist_cm: "", tracker: "", note: "" });
  const [saving, setSaving] = useState(false);

  const load = () =>
    axios.get(`${API_BASE}/diet/client/me`, { withCredentials: true })
      .then((r) => setState({ loading: false, client: r.data, error: null }))
      .catch((e) => setState({ loading: false, client: null, error: e?.response?.status === 401 ? "login" : (e?.response?.data?.detail || "none") }));

  useEffect(() => { load(); }, []);

  const addProgress = async () => {
    if (!p.weight_kg && !p.waist_cm && !p.note.trim()) { toast.error("Enter a weight, waist or note"); return; }
    setSaving(true);
    try {
      await axios.post(`${API_BASE}/diet/progress`, {
        weight_kg: p.weight_kg ? parseFloat(p.weight_kg) : null,
        waist_cm: p.waist_cm ? parseFloat(p.waist_cm) : null,
        tracker: p.tracker || null, note: p.note.trim() || null,
      }, { withCredentials: true });
      toast.success("Progress recorded");
      setP({ weight_kg: "", waist_cm: "", tracker: p.tracker, note: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const download = async (doc) => {
    // Direct navigation works reliably on mobile browsers (blob+download attr fails on iOS Safari)
    window.open(`${API_BASE}/diet/documents/${doc.id}/download`, "_blank", "noopener");
  };

  if (state.loading) return <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center"><Loader2 className="animate-spin text-[#89937C]" /></div>;

  if (!state.client) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] text-[#2D372B]">
        <Nav />
        <div className="max-w-md mx-auto px-6 pt-40 text-center" data-testid="diet-client-empty">
          <h1 className="font-serif-display text-3xl">{state.error === "login" ? "Log in to see your JAY journey" : "No JAY programme yet"}</h1>
          <p className="mt-3 text-sm text-[#5C665A]">
            {state.error === "login" ? "Use the email you purchased with." : "Start with a JAY programme — your journey begins with a personal assessment."}
          </p>
          <a href={state.error === "login" ? "/login" : "/diet#programmes"} className="mt-6 inline-flex rounded-full px-8 py-3.5 bg-[#2D372B] text-white font-medium hover:bg-[#89937C] transition-colors">
            {state.error === "login" ? "Member Login" : "Explore JAY Programmes"}
          </a>
        </div>
      </div>
    );
  }

  const c = state.client;
  const prog = c.programme || {};
  const weights = (c.progress || []).filter((x) => x.weight_kg != null);
  const startW = weights[0]?.weight_kg;
  const curW = weights[weights.length - 1]?.weight_kg;
  const latestDoc = (c.documents || [])[0];
  const activePhaseI = c.phase ? phaseIdx(c.phase.id) : -1;
  const visiblePhases = PHASES.filter((ph, i) => {
    const totalD = prog.days || 15;
    return ph.n === 1 || parseInt(ph.days.match(/\d+/)?.[0] || 0, 10) <= totalD || i <= activePhaseI;
  }).filter((ph) => {
    const startDay = PHASES[ph.n - 1] && [1,16,31,61,91,121,151,181,211,241,271,301][ph.n - 1];
    return startDay <= (prog.days || 15);
  });

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#2D372B]">
      <Nav />
      <div className="max-w-6xl mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-20">
        <a href="/" data-testid="back-to-home-btn"
          className="inline-flex items-center gap-2 rounded-full px-5 py-2 mb-4 border border-[#2D372B]/25 bg-white text-[#2D372B] text-sm font-medium hover:bg-[#2D372B] hover:text-white transition-colors">
          <Home size={14} /> Back to Home
        </a>
        <div className="overline mb-2">My JAY Journey</div>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h1 className="font-serif-display text-4xl md:text-5xl tracking-tighter" data-testid="diet-client-title">
            {prog.name} {c.day ? <span className="text-[#AD6A48]">· Day {c.day} / {c.total_days}</span> : null}
          </h1>
          {latestDoc && (
            <button type="button" data-testid="download-latest-jay7" onClick={() => download(latestDoc)}
              className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 bg-[#C57B57] text-white font-medium hover:bg-[#AD6A48] transition-colors">
              <Download size={15} /> Download My JAY 7™
            </button>
          )}
        </div>

        {c.status === "pending_onboarding" && (
          <div className="mt-6 bg-[#C57B57]/10 border border-[#C57B57]/40 rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3" data-testid="pending-onboarding-banner">
            <p className="text-sm">Your programme is confirmed — complete your assessment to start Day 1.</p>
            <a href="/diet/onboarding" className="rounded-full px-6 py-2.5 bg-[#2D372B] text-white text-sm font-medium">Start Assessment</a>
          </div>
        )}

        {/* Phase progression */}
        {c.day && (
          <div className="mt-8 flex flex-wrap gap-2" data-testid="phase-progression">
            {visiblePhases.map((ph, i) => {
              const state2 = i < activePhaseI ? "done" : i === activePhaseI ? "current" : "next";
              return (
                <span key={ph.n} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium border ${
                  state2 === "done" ? "bg-[#89937C]/15 border-[#89937C]/40 text-[#5C665A]" :
                  state2 === "current" ? "bg-[#2D372B] border-[#2D372B] text-[#E8C9B8]" :
                  "bg-white border-[#E5E2D9] text-[#9AA294]"}`}>
                  {state2 === "done" && <Check size={11} />}{ph.name}{state2 === "current" ? " — CURRENT" : ""}
                </span>
              );
            })}
          </div>
        )}

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            ["Starting weight", startW ? `${startW} kg` : "—"],
            ["Current weight", curW ? `${curW} kg` : "—"],
            ["Change", startW && curW ? `${(curW - startW > 0 ? "+" : "")}${(curW - startW).toFixed(1)} kg` : "—"],
            ["Next review", c.next_review ? new Date(c.next_review).toLocaleDateString() : "—"],
          ].map(([l, v]) => (
            <div key={l} className="bg-white border border-[#E5E2D9] rounded-2xl p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C665A]">{l}</div>
              <div className="font-serif-display text-2xl mt-1">{v}</div>
            </div>
          ))}
        </div>

        {prog.trial && !c.completed && (c.day || 0) >= 5 && (
          <div className="mt-8 bg-[#C57B57] rounded-3xl p-7 flex flex-wrap items-center justify-between gap-4" data-testid="trial-nudge">
            <div>
              <div className="font-serif-display text-xl text-white">Your free trial ends in {Math.max(0, 7 - c.day)} day{7 - c.day === 1 ? "" : "s"}.</div>
              <p className="text-sm text-[#2D372B] mt-1 font-medium">Loving your TRIAL JAY 7? Continue with a full programme — or message your coach with any questions before upgrading.</p>
            </div>
            <div className="flex gap-3">
              <a href="/diet#programmes" data-testid="trial-upgrade-link" className="rounded-full px-6 py-2.5 bg-white text-[#2D372B] text-sm font-medium hover:bg-[#F7F5F0]">See JAY Programmes</a>
              <a href="https://wa.me/919311390365?text=Hi%20Coach%2C%20my%20trial%20is%20ending%20%E2%80%94%20I%20have%20a%20few%20questions%20before%20upgrading" target="_blank" rel="noreferrer" className="rounded-full px-6 py-2.5 border border-white/50 text-white text-sm font-medium hover:bg-white/10">Ask on WhatsApp</a>
            </div>
          </div>
        )}

        {c.completed && (c.upgrade_options || []).length > 0 && (
          <div className="mt-8 bg-[#2D372B] rounded-3xl p-8 text-center" data-testid="upgrade-recommendation">
            <h2 className="font-serif-display text-2xl text-[#F7F5F0]">Ready to keep going?</h2>
            <p className="text-sm text-[#C9CFC0] mt-2">You've completed {prog.name}. Continue your journey:</p>
            <div className="mt-4 flex flex-wrap justify-center gap-3">
              {c.upgrade_options.map((u) => (
                <a key={u.id} href="/diet#programmes" className="rounded-full px-6 py-2.5 bg-[#C57B57] text-white text-sm font-medium hover:bg-[#AD6A48]">{u.name} — {u.tagline}</a>
              ))}
            </div>
          </div>
        )}

        {/* Weight trend */}
        {weights.length >= 2 && (
          <div className="mt-8 bg-white border border-[#E5E2D9] rounded-3xl p-7" data-testid="weight-chart">
            <h2 className="font-serif-display text-2xl mb-4">Weight trend</h2>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={weights.map((w) => ({ date: w.created_at.slice(5, 10), kg: w.weight_kg }))} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E2D9" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#5C665A" }} />
                  <YAxis domain={["dataMin - 1", "dataMax + 1"]} tick={{ fontSize: 10, fill: "#5C665A" }} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5E2D9", fontSize: 12 }} formatter={(v) => [`${v} kg`, "Weight"]} />
                  <Line type="monotone" dataKey="kg" stroke="#C57B57" strokeWidth={2.5} dot={{ r: 3, fill: "#C57B57" }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <div className="mt-10 grid lg:grid-cols-2 gap-8">
          {/* Documents */}
          <section className="bg-white border border-[#E5E2D9] rounded-3xl p-7">
            <h2 className="font-serif-display text-2xl mb-4">My JAY Documents</h2>
            {(c.documents || []).length === 0 ? (
              <p className="text-sm text-[#5C665A]" data-testid="no-documents">Your JAY 7™ Week 1 plan will appear here once your assessment has been reviewed.</p>
            ) : (
              <ul className="space-y-3" data-testid="document-list">
                {c.documents.map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-3 border border-[#E5E2D9] rounded-xl px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText size={17} className="text-[#AD6A48] shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{d.title} <span className="text-[#9AA294]">v{d.version}</span></div>
                        <div className="text-xs text-[#5C665A]">{d.phase ? `${d.phase} · ` : ""}{new Date(d.created_at).toLocaleDateString()}</div>
                      </div>
                    </div>
                    <button type="button" data-testid={`doc-download-${d.id}`} onClick={() => download(d)} className="p-2 rounded-full border border-[#E5E2D9] hover:bg-[#F7F5F0]" aria-label="Download">
                      <Download size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Progress */}
          <section className="bg-white border border-[#E5E2D9] rounded-3xl p-7">
            <h2 className="font-serif-display text-2xl mb-4 flex items-center gap-2"><TrendingDown size={18} className="text-[#89937C]" /> Track My Progress</h2>
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="progress-weight" type="number" placeholder="Weight (kg)" value={p.weight_kg} onChange={(e) => setP({ ...p, weight_kg: e.target.value })} className="bg-[#F7F5F0] h-11 rounded-xl" />
              <Input data-testid="progress-waist" type="number" placeholder="Waist (cm)" value={p.waist_cm} onChange={(e) => setP({ ...p, waist_cm: e.target.value })} className="bg-[#F7F5F0] h-11 rounded-xl" />
            </div>
            <select data-testid="progress-tracker" value={p.tracker} onChange={(e) => setP({ ...p, tracker: e.target.value })} className="mt-3 w-full bg-[#F7F5F0] border border-[#E5E2D9] rounded-xl h-11 px-3 text-sm">
              <option value="">Tracking tool (optional)</option>
              {TRACKERS.map((t) => <option key={t}>{t}</option>)}
            </select>
            <textarea data-testid="progress-note" rows={2} placeholder="Note — energy, hunger, adherence, wins…" value={p.note} onChange={(e) => setP({ ...p, note: e.target.value })}
              className="mt-3 w-full bg-[#F7F5F0] border border-[#E5E2D9] rounded-xl px-3 py-2.5 text-sm resize-none" />
            <button type="button" data-testid="progress-save" onClick={addProgress} disabled={saving}
              className="mt-3 inline-flex items-center gap-2 rounded-full px-6 py-2.5 bg-[#2D372B] text-white text-sm font-medium hover:bg-[#89937C] disabled:opacity-60">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Record
            </button>
            {(c.progress || []).length > 0 && (
              <ul className="mt-5 space-y-2 max-h-44 overflow-y-auto pr-1">
                {[...c.progress].reverse().slice(0, 10).map((e) => (
                  <li key={e.id} className="text-xs text-[#5C665A] flex justify-between gap-2 border-b border-[#F0EEE6] pb-1.5">
                    <span>{e.weight_kg ? `${e.weight_kg} kg` : ""}{e.waist_cm ? ` · ${e.waist_cm} cm` : ""}{e.note ? ` · ${e.note}` : ""}</span>
                    <span className="shrink-0">{new Date(e.created_at).toLocaleDateString()}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <TrackerSection />

        {/* Support */}
        <div className="mt-8 bg-[#2D372B] rounded-3xl p-7 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="font-serif-display text-xl text-[#F7F5F0]">Need help or a plan tweak?</div>
            <p className="text-sm text-[#C9CFC0] mt-1">Your coach is a message away — WhatsApp or email any time.</p>
          </div>
          <div className="flex gap-3">
            <a data-testid="support-whatsapp" href="https://wa.me/919311390365?text=Hi%20Coach%2C%20a%20question%20about%20my%20JAY%20plan" target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 bg-[#25D366] text-white text-sm font-medium hover:opacity-90"><MessageCircle size={15} /> WhatsApp</a>
            <a data-testid="support-email" href="mailto:sanbhan1812@gmail.com?subject=JAY%20DIETS%20support"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 border border-white/30 text-white text-sm font-medium hover:bg-white/10"><Mail size={15} /> Email</a>
          </div>
        </div>
      </div>
    </div>
  );
}
